import { NavigationMeshRef } from '../scene/SceneManifest';
import { NavigationMeshReader } from './NavigationMeshReader';
import { NavigationSystem } from './NavigationSystem';
import { PathNode } from './PathNode';
import { TileArchiveCache } from './TileArchive';
import {
  TileParseWorkerClient,
  reconstructNavmeshNodes,
} from '../workers/TileParseWorkerClient';

type ResourceLoader = {
  loadResource(path: string): Promise<Uint8Array>;
};

/**
 * Camera-driven tiled navmesh loader. Maintains a 3×3 ring of loaded tiles
 * around the camera's current tile. Tile arrivals and evictions trigger a full
 * spatial re-stitch: all loaded tiles are re-stitched from cached PathNode arrays,
 * cross-tile neighbour links are derived by XZ-overlap + step-height check, and
 * the NavigationSystem is updated in-place via `rebuildWith()`.
 *
 * Cross-tile links are NOT stored in the manifest; they are computed here from
 * the node bounding boxes using the same logic the backend used to verify them.
 *
 * Parsed nodes are cached per-tile (parse once on load, not on every rebuild).
 * Before each re-stitch, cross-tile neighbours are truncated back to the
 * intra-tile-only count recorded at parse time.
 */
export class TiledNavMeshStreamer {
  private readonly tilesX: number;
  private readonly tilesZ: number;
  private readonly tileWorldSize: number;
  private readonly worldMinX: number;
  private readonly worldMinZ: number;
  private readonly pathPrefix: string;
  private readonly stitchStepHeight: number;
  private readonly archiveCache: TileArchiveCache;

  /** Parsed PathNode arrays per tile key "tx,tz" — intra-tile links only at parse time. */
  private nodeCache = new Map<string, PathNode[]>();
  /** neighbours.length snapshot taken right after parsing (= intra-tile count). */
  private intraCounts = new Map<string, number[]>();
  /** node.id snapshot taken right after parsing (= tile-local IDs from file). */
  private originalIds = new Map<string, number[]>();

  private loading = new Set<string>();
  private rebuildPending = false;

  private lastCameraTileX = -999;
  private lastCameraTileZ = -999;

  constructor(
    private readonly ref: NavigationMeshRef,
    private readonly navigation: NavigationSystem,
    resourceLoader: ResourceLoader,
    archiveCache?: TileArchiveCache,
    private readonly workerClient?: TileParseWorkerClient,
  ) {
    this.tilesX = ref.tilesX;
    this.tilesZ = ref.tilesZ;
    this.tileWorldSize = ref.tileWorldSize;
    this.worldMinX = ref.worldMinX;
    this.worldMinZ = ref.worldMinZ;
    this.pathPrefix = ref.pathPrefix;
    this.stitchStepHeight = ref.settings.crossTileStitchStepHeight;
    this.archiveCache = archiveCache ?? new TileArchiveCache(resourceLoader, this.pathPrefix);
  }

  /** Shared archive cache so other tile streamers reuse the same fetch. */
  getArchiveCache(): TileArchiveCache {
    return this.archiveCache;
  }

  /** Call each frame with camera world position to trigger tile loading/eviction. */
  updateCamera(x: number, z: number): void {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);

    if (tx === this.lastCameraTileX && tz === this.lastCameraTileZ) return;
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;

    const needed = new Set<string>();
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (ntx >= 0 && ntx < this.tilesX && ntz >= 0 && ntz < this.tilesZ) {
          needed.add(`${ntx},${ntz}`);
        }
      }
    }

    let changed = false;

    // Evict tiles outside the ring
    for (const key of this.nodeCache.keys()) {
      if (!needed.has(key)) {
        this.evictTile(key);
        changed = true;
      }
    }

    // Load missing tiles
    for (const key of needed) {
      if (!this.nodeCache.has(key) && !this.loading.has(key)) {
        this.loadTile(key);
      }
    }

    if (changed) {
      this.scheduleRebuild();
    }
  }

  /**
   * Start loading tiles around a starting position. Returns immediately — tiles
   * arrive async and trigger a single rebuild once the batch completes. Navigation
   * is unavailable until at least one tile loads; callers must not block on this.
   */
  startInitialTiles(x: number, z: number): void {
    void this.loadTilesAround(x, z);
  }

  /**
   * Load the 3×3 ring of nav-mesh tiles around (x, z) and wait for navigation
   * to be live. Use this when the navmesh is needed synchronously — e.g.
   * spawning the player into a region the freecam flew to but the per-frame
   * streamer update hasn't yet covered (the streamer is driven by the player
   * position, not the freecam, so freecam regions never load tiles).
   */
  async loadTilesAround(x: number, z: number): Promise<void> {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;

    const keys: string[] = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (ntx >= 0 && ntx < this.tilesX && ntz >= 0 && ntz < this.tilesZ) {
          const key = `${ntx},${ntz}`;
          if (!this.nodeCache.has(key) && !this.loading.has(key)) {
            keys.push(key);
          }
        }
      }
    }
    await Promise.all(keys.map((k) => this.fetchTile(k)));
    this.rebuildNavigation();
  }

  /** Fetch and parse one tile; store nodes in cache. Does not trigger a rebuild. */
  private async fetchTile(key: string): Promise<void> {
    this.loading.add(key);
    const [txStr, tzStr] = key.split(',');
    const tx = parseInt(txStr);
    const tz = parseInt(tzStr);
    try {
      const archive = await this.archiveCache.get(tx, tz);
      if (!archive) return;
      const navBytes = archive.navmesh;
      // Ensure an isolated, cleanly aligned buffer (subarray views can have
      // non-zero byteOffset which breaks DataView usage in the reader).
      const navBuffer = navBytes.byteOffset === 0 && navBytes.byteLength === navBytes.buffer.byteLength
        ? (navBytes.buffer as ArrayBuffer)
        : navBytes.slice().buffer;

      let nodes: PathNode[];
      if (this.workerClient) {
        // Transfer the buffer to the worker; it comes back as flat typed arrays.
        const result = await this.workerClient.parseNavmesh(navBuffer);
        nodes = reconstructNavmeshNodes(result);
      } else {
        nodes = NavigationMeshReader.read(navBuffer);
      }

      this.nodeCache.set(key, nodes);
      this.intraCounts.set(key, nodes.map((n) => n.neighbours.length));
      this.originalIds.set(key, nodes.map((n) => n.id));
      // Add to the octree immediately so the index stays current; the rebuild
      // only needs to re-stitch and refresh the pathfinder (no full octree clear).
      this.navigation.addNodes(nodes);
    } catch (e) {
      console.warn(`Failed to load navmesh tile (${tx},${tz}):`, e);
    } finally {
      this.loading.delete(key);
    }
  }

  /** Fetch a tile and schedule a rebuild (used by runtime camera streaming). */
  private async loadTile(key: string): Promise<void> {
    await this.fetchTile(key);
    if (this.nodeCache.has(key)) {
      this.scheduleRebuild();
    }
  }

  private evictTile(key: string): void {
    const evicted = this.nodeCache.get(key);
    if (evicted) this.navigation.removeNodes(evicted);
    this.nodeCache.delete(key);
    this.intraCounts.delete(key);
    this.originalIds.delete(key);
    const [txStr, tzStr] = key.split(',');
    this.archiveCache.evict(parseInt(txStr), parseInt(tzStr));
  }

  /**
   * Schedule a navigation rebuild as a microtask. Multiple calls within the same
   * synchronous turn coalesce into a single rebuild, so rapid evictions and
   * concurrent tile arrivals never stall a frame with back-to-back rebuilds.
   */
  private scheduleRebuild(): void {
    if (this.rebuildPending) return;
    this.rebuildPending = true;
    queueMicrotask(() => {
      this.rebuildPending = false;
      this.rebuildNavigation();
    });
  }

  /**
   * Re-stitch all cached tiles and update the NavigationSystem. Cached PathNode
   * arrays are reused — cross-tile neighbours are truncated back to the
   * intra-tile-only count before re-stitching, so no buffer re-parsing occurs.
   */
  private rebuildNavigation(): void {
    const tileNodes = new Map<string, PathNode[]>();
    let globalIdOffset = 0;

    for (const [key, nodes] of this.nodeCache) {
      const intra = this.intraCounts.get(key)!;
      const origIds = this.originalIds.get(key)!;
      // Reset to intra-tile-only state: drop cross-tile neighbours from last stitch,
      // restore tile-local IDs so the globalIdOffset is applied from a known base.
      for (let i = 0; i < nodes.length; i++) {
        nodes[i].neighbours.length = intra[i];
        nodes[i].id = globalIdOffset + origIds[i];
      }
      globalIdOffset += nodes.length;
      tileNodes.set(key, nodes);
    }

    // Stitch adjacent tile pairs
    for (const [key, nodesA] of tileNodes) {
      const [txStr, tzStr] = key.split(',');
      const tx = parseInt(txStr);
      const tz = parseInt(tzStr);
      const eastKey = `${tx + 1},${tz}`;
      const southKey = `${tx},${tz + 1}`;
      const nodesEast = tileNodes.get(eastKey);
      const nodesSouth = tileNodes.get(southKey);
      if (nodesEast) this.stitchBoundary(nodesA, nodesEast, tx, tz, tx + 1, tz);
      if (nodesSouth) this.stitchBoundary(nodesA, nodesSouth, tx, tz, tx, tz + 1);
    }

    // Collect all nodes. Pre-allocate to avoid spread-operator argument-stack
    // overflow and repeated array growth at 48k-70k node counts.
    let totalNodes = 0;
    for (const nodes of tileNodes.values()) totalNodes += nodes.length;
    const allNodes = new Array<PathNode>(totalNodes);
    let off = 0;
    for (const nodes of tileNodes.values()) {
      for (let i = 0; i < nodes.length; i++) allNodes[off++] = nodes[i];
    }

    // Octree is already current (addNodes on load, removeNodes on evict).
    // Only refresh the pathfinder (region graph) after re-stitching.
    this.navigation.syncPathFinder(allNodes);
  }

  /**
   * Link border nodes between two adjacent tiles.
   * Mirrors TiledNavigationMeshBuilder.matchBorderNodes on the backend:
   * nodes whose bounding boxes share an XZ edge and whose Y floors are within
   * `stitchStepHeight` become neighbours.
   */
  private stitchBoundary(
    nodesA: PathNode[],
    nodesB: PathNode[],
    txA: number,
    tzA: number,
    txB: number,
    tzB: number,
  ): void {
    const minXA = this.worldMinX + txA * this.tileWorldSize;
    const minZA = this.worldMinZ + tzA * this.tileWorldSize;
    const maxXA = Math.min(minXA + this.tileWorldSize, this.worldMinX + this.tilesX * this.tileWorldSize);
    const maxZA = Math.min(minZA + this.tileWorldSize, this.worldMinZ + this.tilesZ * this.tileWorldSize);

    const eps = this.ref.settings.voxelSize;
    const step = this.stitchStepHeight;

    const borderA = nodesA.filter((n) => {
      if (txB === txA + 1) return n.max[0] >= maxXA - eps;
      if (tzB === tzA + 1) return n.max[2] >= maxZA - eps;
      return false;
    });

    const borderB = nodesB.filter((n) => {
      if (txB === txA + 1) return n.min[0] <= this.worldMinX + txB * this.tileWorldSize + eps;
      if (tzB === tzA + 1) return n.min[2] <= this.worldMinZ + tzB * this.tileWorldSize + eps;
      return false;
    });

    for (const a of borderA) {
      for (const b of borderB) {
        if (this.nodesOverlap(a, b, step)) {
          a.addNeighbour(b);
        }
      }
    }
  }

  /**
   * Returns true when two nodes' XZ bounding boxes overlap (or touch) and their
   * floor heights are within `stepHeight` of each other.
   * Uses strict `<` (not `<=`) so nodes that share an exact tile boundary edge
   * (A.max[0] === B.min[0]) are still considered overlapping — matching the
   * backend's `nodesOverlap` in TiledNavigationMeshBuilder.
   */
  private nodesOverlap(a: PathNode, b: PathNode, stepHeight: number): boolean {
    if (a.max[0] < b.min[0] || b.max[0] < a.min[0]) return false;
    if (a.max[2] < b.min[2] || b.max[2] < a.min[2]) return false;
    return Math.abs(a.min[1] - b.min[1]) <= stepHeight;
  }
}
